/**
 * A página do Recibo de Pagamento, aberta numa aba nova.
 *
 * Papel: reproduz `gerarReciboAjudaCusto` (l. 5382–5459) como página: as duas
 * vias iguais do recibo — a do colaborador e a do pagador — separadas pela
 * linha de corte, montadas a partir de `GET financas/documentos/recibo`, e a
 * impressão chamada assim que o recibo chega.
 *
 * Como usar: o `router.tsx` a monta em `/financas/recibo/lancamento/:lancamentoId`
 * (o 🧾 da linha de Lançamentos) e em
 * `/financas/recibo/outros-bancos/:favorecidoId/:competencia` (o 🧾 do cartão de
 * Outros Bancos), dentro do `DocumentoProtegido`, sem o `Layout`. Os itens já
 * chegam com o 1/3 de férias destrinchado, e o total é o que o corpo escreve.
 *
 * O lançamento que não existe mostra "Lançamento não encontrado" no lugar do
 * recibo, sem "Tentar de novo". As outras recusas da API (4xx) — o recibo sem
 * valor, o favorecido que não existe, a falta de permissão — mostram a frase
 * dela, também sem "Tentar de novo", que nunca daria certo. Só a falha do
 * servidor (5xx) ou da rede oferece tentar de novo.
 */
import { type ConsultaDoRecibo, type ReciboDePagamento } from '@concierge/shared';
import { useParams } from 'react-router';

import { ErroDaApi } from '@/shared/api';
import { formatarCpf } from '@/shared/lib/formato';
import { valorPorExtenso } from '@/shared/lib/extenso';
import {
  AvisoDoDocumento,
  FolhaDoDocumento,
  ItensDoDocumento,
  TopoDoDocumento,
} from '../components/documento';
import { dataPorExtenso, etiquetaDaSegundaVia, referenteA } from '../documentos';
import { formatarReais } from '../formato';
import { useRecibo } from '../hooks/usar-financas-extras';
import { useDocumentoPronto } from '../hooks/use-documento-pronto';

/**
 * Uma via do recibo.
 *
 * @param recibo - O recibo.
 * @param etiqueta - "1ª via — Colaborador(a)" ou a da segunda via.
 * @returns A via, que não se parte entre duas páginas.
 */
function ViaDoRecibo({ recibo, etiqueta }: { recibo: ReciboDePagamento; etiqueta: string }) {
  return (
    <div className="break-inside-avoid px-[3mm] pt-[5mm] pb-[3mm]">
      <div className="mb-3 text-right font-documento-rotulo text-[9px] tracking-[0.08em] text-emb-doc-rotulo-claro uppercase">
        {etiqueta}
      </div>
      <TopoDoDocumento
        titulo="Recibo de Pagamento"
        referencia={referenteA(recibo.competencia)}
        className="mb-5"
      />
      <div className="mb-4 text-center text-[13px] font-bold tracking-[1px] uppercase">
        {recibo.favorecido}
      </div>
      <p className="m-0 mb-5 text-justify leading-[1.7]">
        Recebi de {recibo.pagador}, a quantia de {formatarReais(recibo.total)} (
        {valorPorExtenso(recibo.total)}), referente ao descritivo abaixo.
      </p>
      <ItensDoDocumento itens={recibo.itens} className="mb-[26px]" />
      <div className="mt-[22px] mb-[38px]">{dataPorExtenso(recibo.data)}</div>
      <div className="text-center">
        <div className="mx-auto mb-1.5 w-[300px] border-t border-emb-doc-traco" />
        <div>{recibo.favorecido}</div>
        <div className="mt-0.5 text-[11px] text-emb-doc-texto-fraco">
          CPF: {recibo.cpf === null ? '' : formatarCpf(recibo.cpf)}
        </div>
      </div>
    </div>
  );
}

/**
 * O recorte do recibo pela rota.
 *
 * @param parametros - Os parâmetros da rota.
 * @returns O lançamento, ou o favorecido e a competência.
 */
function consultaDaRota(
  parametros: Readonly<Record<string, string | undefined>>,
): ConsultaDoRecibo {
  const { lancamentoId, favorecidoId, competencia } = parametros;
  return lancamentoId !== undefined
    ? { lancamentoId }
    : { favorecidoId: favorecidoId ?? '', competencia: competencia ?? '' };
}

/**
 * Página do recibo.
 *
 * @returns As duas vias, ou a linha de espera, de falha ou de não encontrado.
 */
export function PaginaRecibo() {
  const recibo = useRecibo(consultaDaRota(useParams()));
  useDocumentoPronto(recibo.data === undefined ? null : `Recibo — ${recibo.data.favorecido}`);

  if (recibo.data === undefined) {
    const erro = recibo.error;
    if (erro instanceof ErroDaApi && erro.status === 404) {
      return <AvisoDoDocumento texto="Lançamento não encontrado" />;
    }
    if (erro instanceof ErroDaApi && erro.status >= 400 && erro.status < 500) {
      return <AvisoDoDocumento texto={erro.message} />;
    }
    return recibo.isError ? (
      <AvisoDoDocumento
        texto="Não foi possível carregar."
        onTentarDeNovo={() => void recibo.refetch()}
      />
    ) : (
      <AvisoDoDocumento texto="Carregando…" />
    );
  }

  return (
    <FolhaDoDocumento className="px-[15mm] py-[16mm]">
      <ViaDoRecibo recibo={recibo.data} etiqueta="1ª via — Colaborador(a)" />
      <div className="my-[9mm] flex items-center gap-2.5 font-documento-rotulo text-[9.5px] tracking-[0.12em] text-emb-doc-rotulo-claro uppercase">
        <span className="flex-1 border-t border-dashed border-emb-doc-corte" />
        <span>✂ corte aqui ✂</span>
        <span className="flex-1 border-t border-dashed border-emb-doc-corte" />
      </div>
      <ViaDoRecibo recibo={recibo.data} etiqueta={etiquetaDaSegundaVia(recibo.data.pagador)} />
    </FolhaDoDocumento>
  );
}
